import { useTranslation } from 'react-i18next';
import { ArrowRight, Cpu, Brain, Zap, Layers, Code2, Terminal, Shield, BarChart3 } from 'lucide-react';
import TopBar from '../../components/TopBar';
import PageTitle from '../../components/PageTitle';
import SiteFooter from '../../components/SiteFooter';

const LLM_URL = import.meta.env.VITE_LLM_URL || 'https://llm.etherworks.cn';

const capIcons = [Brain, Zap, Layers, Code2, Terminal, Shield, BarChart3, Cpu];

export default function ReverseModel() {
  const { t } = useTranslation('products');

  const capabilities = capIcons.map((icon, i) => ({
    icon,
    title: t(`reverse_model.cap${i + 1}_title`),
    desc: t(`reverse_model.cap${i + 1}_desc`),
  }));

  const competitors = [
    t('reverse_model.bench_col_ours'),
    'Qwen 3.5',
    'DeepSeek-R1-0528',
    'Gemini 3.1 Pro',
    'Claude Opus 4.6',
    'GPT 5.3',
  ];

  const benchmarks: { name: string; scores: (string | null)[] }[] = [
    { name: 'GPQA-Diamond', scores: ['86.0', '88.4', '82.4', '94.3', '91.3', null] },
    { name: 'HLE', scores: ['30.5', '28.7', '25.1', '44.4', '40.0', null] },
    { name: 'SWE-bench Verified', scores: ['77.8', '76.4', '73.1', '80.6', '80.8', null] },
    { name: 'Terminal-Bench 2.0', scores: ['56.2', '52.5', '39.3', '68.5', '65.4', '77.3'] },
    { name: 'BrowseComp', scores: ['62.0', '69.0', '51.4', '85.9', '84.0', null] },
    { name: 'τ²-Bench', scores: ['89.7', '86.7', '85.3', '90.8', '91.9', null] },
  ];

  const getBestIdx = (scores: (string | null)[]) => {
    let best = -1, bestVal = -Infinity;
    scores.forEach((s, i) => {
      const v = parseFloat(s || '');
      if (!isNaN(v) && v > bestVal) { bestVal = v; best = i; }
    });
    return best;
  };

  return (
    <div className="min-h-screen bg-background">
      <PageTitle 
        titleKey="reverse_model.title" 
        namespace="products" 
        descriptionKey="reverse_model.subtitle"
      />
      <TopBar />

      {/* Hero */}
      <section className="relative pt-24 pb-8 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Decorative background */}
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/[0.03] via-transparent to-transparent dark:from-emerald-500/[0.06]" />
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(16,185,129,0.07) 1px, transparent 0)', backgroundSize: '32px 32px' }} />

        {/* Floating decorative orbs */}
        <div className="absolute top-16 left-[10%] w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-4 right-[8%] w-56 h-56 bg-teal-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />

        {/* Decorative circuit illustration - left */}
        <svg className="absolute left-0 top-1/2 -translate-y-1/2 w-48 h-64 text-emerald-500/10 hidden lg:block" viewBox="0 0 192 256" fill="none">
          <path d="M192 32 H120 L80 72 H40 L20 52" stroke="currentColor" strokeWidth="1.5" />
          <path d="M192 80 H140 L100 120 H60" stroke="currentColor" strokeWidth="1.5" />
          <path d="M192 128 H100 L60 168 H20" stroke="currentColor" strokeWidth="1.5" />
          <path d="M192 176 H130 L90 216 H50" stroke="currentColor" strokeWidth="1.5" />
          <path d="M192 224 H150 L120 244" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="20" cy="52" r="4" fill="currentColor" className="animate-pulse" />
          <circle cx="60" cy="120" r="4" fill="currentColor" className="animate-pulse" style={{ animationDelay: '0.5s' }} />
          <circle cx="20" cy="168" r="4" fill="currentColor" className="animate-pulse" style={{ animationDelay: '1s' }} />
          <circle cx="50" cy="216" r="4" fill="currentColor" className="animate-pulse" style={{ animationDelay: '1.5s' }} />
          <rect x="36" y="48" width="8" height="8" rx="1" stroke="currentColor" strokeWidth="1" />
          <rect x="76" y="116" width="8" height="8" rx="1" stroke="currentColor" strokeWidth="1" />
        </svg>

        {/* Decorative circuit illustration - right */}
        <svg className="absolute right-0 top-1/2 -translate-y-1/2 w-48 h-64 text-teal-500/10 hidden lg:block" viewBox="0 0 192 256" fill="none" style={{ transform: 'translateY(-50%) scaleX(-1)' }}>
          <path d="M192 32 H120 L80 72 H40 L20 52" stroke="currentColor" strokeWidth="1.5" />
          <path d="M192 80 H140 L100 120 H60" stroke="currentColor" strokeWidth="1.5" />
          <path d="M192 128 H100 L60 168 H20" stroke="currentColor" strokeWidth="1.5" />
          <path d="M192 176 H130 L90 216 H50" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="20" cy="52" r="4" fill="currentColor" className="animate-pulse" style={{ animationDelay: '0.7s' }} />
          <circle cx="60" cy="120" r="4" fill="currentColor" className="animate-pulse" style={{ animationDelay: '1.2s' }} />
          <circle cx="20" cy="168" r="4" fill="currentColor" className="animate-pulse" style={{ animationDelay: '1.7s' }} />
        </svg>

        <div className="relative max-w-4xl mx-auto text-center">
          {/* Icon */}
          <div className="inline-flex h-16 w-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 items-center justify-center mb-6 shadow-lg shadow-emerald-500/20">
            <Cpu className="w-8 h-8 text-white" />
          </div>

          {/* Title */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter mb-4 whitespace-nowrap">
            <span className="bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600 bg-clip-text text-transparent">
              {t('reverse_model.name')}
            </span>
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground font-medium mb-8">{t('reverse_model.subtitle')}</p>

          <p className="text-lg text-foreground/80 leading-relaxed max-w-3xl mx-auto mb-4">
            {t('reverse_model.desc_p1')}
          </p>
          <p className="text-base text-muted-foreground leading-relaxed max-w-2xl mx-auto mb-8">
            {t('reverse_model.desc_p2')}
          </p>

          <div className="flex justify-center flex-wrap gap-4">
          </div>
        </div>
      </section>

      {/* Core Capabilities */}
      <section className="pt-8 pb-16 px-4 sm:px-6 lg:px-8 bg-muted/30">

        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tighter mb-3 text-center">{t('reverse_model.capabilities_title')}</h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            {t('reverse_model.capabilities_desc')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {capabilities.map((cap) => {
              const Icon = cap.icon;
              return (
                <div key={cap.title} className="group relative rounded-[1.25rem] border border-border p-2 md:p-3 transition-transform hover:scale-[1.02]">
                  <div className="relative flex h-full flex-col gap-3 overflow-hidden rounded-xl border border-border bg-background p-6 shadow-sm dark:shadow-[0px_0px_27px_0px_rgba(45,45,45,0.3)]">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <h3 className="text-base font-semibold text-foreground tracking-tight">{cap.title}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">{cap.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benchmarks Comparison */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tighter mb-3 text-center">{t('reverse_model.benchmarks_title')}</h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            {t('reverse_model.benchmarks_desc')}
          </p>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50">
                  <th className="text-left px-4 py-3 font-semibold text-foreground sticky left-0 bg-muted/50 z-10 min-w-[160px]">
                    {t('reverse_model.bench_col_benchmark')}
                  </th>
                  {competitors.map((name, i) => (
                    <th
                      key={name}
                      className={`px-4 py-3 text-center font-semibold min-w-[100px] ${i === 0
                        ? 'text-primary bg-primary/5'
                        : 'text-muted-foreground'
                        }`}
                    >
                      {name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {benchmarks.map((bm) => {
                  const bestIdx = getBestIdx(bm.scores);
                  return (
                    <tr key={bm.name} className="hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-3 font-medium text-foreground sticky left-0 bg-background z-10">
                        {bm.name}
                      </td>
                      {bm.scores.map((score, i) => (
                        <td
                          key={i}
                          className={`px-4 py-3 text-center tabular-nums ${i === bestIdx
                            ? 'font-bold text-primary'
                            : i === 0
                              ? 'font-medium text-foreground bg-primary/5'
                              : 'text-muted-foreground'
                            }`}
                        >
                          {score ?? '—'}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground mt-4 text-center">
            {t('reverse_model.bench_footnote')}
          </p>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
